import { RheologyDataDto, ThroughputControlType } from 'providers/api';
import { Model } from '../hooks/useModelState';
import PCRoute from '../MineModel/Route/PCRoute';
import { OptimisationTargetType, RecipeState } from '../MineModel/types';
import { calculateFrictionForRoute } from '../canvas/Model/updateFriction';
import { updateRecipeState } from '../canvas/Model/updateRecipeState';
import { assembleTargetLineWarnings, calculateTargetLineResult } from '../canvas/PCFunctions/PCPasteFunctions';
import { buildCandidateRoute } from './routeSegments';
import { segmentLoads } from './segmentCheck';
import {
  Assignment,
  AutoPipeAssignerSettings,
  SegmentInfo,
  SegmentLoad,
  StopeEvaluation
} from './types';

export interface StopeSolveContext {
  model: Model;
  rheology: RheologyDataDto;
  settings: AutoPipeAssignerSettings;
}

// The same state optimiseAutoRecipeForm builds for a solved mix, so the route is priced exactly as the solver priced it.
export const recipeStateAt = (
  model: Model,
  rheology: RheologyDataDto,
  concentrationByMass: number,
  binderConcentrationByMass: number,
  throughput: number,
  throughputControlType: ThroughputControlType,
): RecipeState => updateRecipeState(
  model,
  {
    totalDryConcentrationByMass: concentrationByMass,
    binderConcentrationByMass,
    tailingsSolidsDensity: rheology.tailingsDrySolidsDensity,
    binderParticleDensity: rheology.binderDryParticleDensity,
    carrierFluidDensity: rheology.carrierFluidDensity,
    throughputControlType,
    tailingsDryTonnage: throughputControlType === ThroughputControlType.DryTonnage ? throughput : undefined,
    wetTonnage: throughputControlType === ThroughputControlType.WetTonnage ? throughput : undefined,
    wetFlowRate: throughputControlType === ThroughputControlType.FlowRate ? throughput : undefined,
    rheologyDataSetId: rheology.rheologyDataSetId,
  },
  concentrationByMass,
  binderConcentrationByMass,
);

const peakPressureByOwner = (loads: SegmentLoad[]): Record<string, number> => loads.reduce<Record<string, number>>(
  (acc, { ownerSpoolId, pressureKpa }) => (ownerSpoolId
    ? { ...acc, [ownerSpoolId]: Math.max(acc[ownerSpoolId] ?? 0, pressureKpa) }
    : acc),
  {},
);

const peakShareOfRating = (loads: SegmentLoad[]): number => loads.reduce(
  (peak, { pressureKpa, ratingKpa }) => (pressureKpa / ratingKpa > peak ? pressureKpa / ratingKpa : peak),
  0,
);

/**
 * Solves the stope's paste mix at the pump pressure on the candidate pipes, then checks every segment of the route
 * against its pipe rating. The highest concentration the pump can deliver is the worst case for pipe pressure.
 */
export const evaluateStope = (
  { model, rheology, settings }: StopeSolveContext,
  route: PCRoute,
  segments: SegmentInfo[],
  assignment: Assignment,
): StopeEvaluation => {
  const {
    pumpPressureBar,
    throughput,
    targetUCS,
    curingDays,
    allowancePercent,
    excludedPipeIds,
  } = settings;
  const throughputControlType = model.project.throughput;
  const candidate = buildCandidateRoute(route, segments, assignment, model.pipes);

  const { result } = calculateTargetLineResult(
    model,
    rheology,
    { targetType: OptimisationTargetType.PumpPressure, pumpPressureTarget: pumpPressureBar },
    pumpPressureBar,
    throughput,
    candidate,
    throughputControlType,
    curingDays,
    targetUCS,
  );
  if (!result) return { reachable: false, reason: 'unreachable' };

  // The solver's own route snapshot is a structuredClone without prototypes, so the candidate is re-priced at the mix.
  const recipeState = recipeStateAt(model, rheology, result.concentrationByMass, result.cement, throughput, throughputControlType);
  const priced = calculateFrictionForRoute(model, recipeState, candidate, rheology);
  const pumpPressureKpa = priced.pumpPressureKpa ?? 0;

  if (assembleTargetLineWarnings(model, priced, pumpPressureKpa, pumpPressureBar, recipeState.wetDensity).includes('BP')) {
    return { reachable: false, reason: 'boosterLimit' };
  }

  const loads = segmentLoads({
    pressures: priced.routePoints.map((point) => point.pressure ?? 0),
    pipes: priced.spools.map((spool) => spool.pipe),
    segments,
    offsetKpa: (pumpPressureKpa * allowancePercent) / 100,
    excludedPipeIds,
  });

  return {
    reachable: true,
    concentrationByMass: result.concentrationByMass,
    pumpPressureKpa,
    peakShareOfRating: peakShareOfRating(loads),
    overpressured: loads.filter((load) => load.overpressured),
    peakPressureByOwner: peakPressureByOwner(loads),
  };
};
